import { useUser } from '@clerk/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActivityEntryDto, DashboardDto, TaskDto } from '@/api/generated';
import { useActivity, useDashboard, useInvoices, usePhases, useTasks } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { TimelineStrip } from '@/design-system/composites/TimelineStrip/TimelineStrip';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { InvoiceCreateDialog } from '@/features/budget/InvoiceDialogs';
import { DecisionCreateDialog } from '@/features/decisions/DecisionDialogs';
import { DocumentUploadDialog } from '@/features/documents/DocumentDialogs';
import { PhotoUploadDialog } from '@/features/werf/PhotoDialogs';
import { TaskCreateDialog } from '@/features/werf/TaskDialogs';
import {
  dutchVerb,
  formatCurrencyEUR,
  formatDayMonth,
  formatRelativeDeadline,
  formatRelativePast,
  iconForVerb,
  parsePayload,
  toNumber,
} from '@/lib/format';
import styles from './Dashboard.module.css';

const TASK_ICON_COLOR: Record<'bad' | 'warn' | 'idle', string> = {
  bad: 'var(--bad)',
  warn: 'var(--warn)',
  idle: 'var(--ink-3)',
};

const DUE_TONE_CLASS: Record<'bad' | 'warn' | 'idle', string> = {
  bad: 'c-bad',
  warn: 'c-warn',
  idle: 'c-idle',
};

// 2026-05-17 — Dashboard timeline header used to hard-code
// "10 fases · 32 maanden" and "JUNI 2024 — NOV 2026". Both come from the
// real phase set now: count = phases.length, span = min(start) → max(end)
// rendered in nl-BE month/year. Hides the metadata line entirely when no
// phases exist (e.g. brand-new project before its first sign-in).
function DashboardTimelineHeader() {
  const phases = usePhases();
  const rows = phases.data ?? [];
  if (rows.length === 0) {
    return (
      <div className={`row ${styles.timelineHeader}`}>
        <h3>Tijdlijn</h3>
      </div>
    );
  }
  const starts = rows.map((p) => Date.parse(p.start)).filter(Number.isFinite);
  const ends = rows.map((p) => Date.parse(p.end)).filter(Number.isFinite);
  const minStart = starts.length > 0 ? new Date(Math.min(...starts)) : null;
  const maxEnd = ends.length > 0 ? new Date(Math.max(...ends)) : null;
  const span = minStart && maxEnd ? `${monthYear(minStart)} — ${monthYear(maxEnd)}` : '';
  const months =
    minStart && maxEnd
      ? Math.max(
          0,
          Math.round((maxEnd.getTime() - minStart.getTime()) / (1000 * 60 * 60 * 24 * 30.44)),
        )
      : 0;
  return (
    <div className={`row ${styles.timelineHeader}`}>
      <h3>Tijdlijn</h3>
      <span className={styles.timelineMeta}>
        {rows.length} fases{months > 0 ? ` · ${months} maanden` : ''}
      </span>
      {span && <span className={`mono ml-auto ${styles.timelineSpan}`}>{span}</span>}
    </div>
  );
}

function monthYear(d: Date): string {
  return d.toLocaleDateString('nl-BE', { month: 'short', year: 'numeric' }).toUpperCase();
}

function dueTone(due: string): 'bad' | 'warn' | 'idle' {
  if (due.includes('te laat')) return 'bad';
  if (due.startsWith('over 1 ') || due.startsWith('over 2 ') || due.startsWith('over 3 ')) {
    return 'bad';
  }
  if (due.startsWith('over 4') || due.startsWith('over 5') || due.startsWith('over 6')) {
    return 'warn';
  }
  if (due === 'vandaag' || due === 'morgen') return 'bad';
  return 'idle';
}

function taskTone(task: TaskDto): 'bad' | 'warn' | 'idle' {
  if (task.urgent) return 'bad';
  if (task.status === 'InProgress') return 'warn';
  return 'idle';
}

export function Dashboard() {
  const dashboard = useDashboard();
  const tasks = useTasks({ status: 'Open', pageSize: 5 });
  const activity = useActivity(1);
  // 2026-06-01 — the budget tile used to read the stored snapshot
  // `budget.spent`, which the backend only recomputes on invoice approval
  // (and counts Paid only). That made it disagree with the Budget page,
  // which aggregates live from invoices. Mirror the Budget page here: pull
  // Open + Paid invoices and compute "besteed" client-side so the two
  // always correspond. See web/src/features/budget/Budget.tsx.
  const openInvoices = useInvoices({ status: 'Open', pageSize: 500 });
  const paidInvoices = useInvoices({ status: 'Paid', pageSize: 500 });
  const paidTotal = (paidInvoices.data?.items ?? []).reduce((s, i) => s + toNumber(i.amount), 0);
  const openTotal = (openInvoices.data?.items ?? []).reduce((s, i) => s + toNumber(i.amount), 0);

  return (
    <AppFrame>
      <QueryBoundary query={dashboard}>{(data) => <DashboardHero data={data} />}</QueryBoundary>

      <QueryBoundary query={dashboard}>
        {(data) => (
          <KpiGrid
            data={data}
            tasksData={tasks.data}
            tasksLoading={tasks.isPending}
            paidTotal={paidTotal}
            openTotal={openTotal}
          />
        )}
      </QueryBoundary>

      <Card className={styles.timelineCard}>
        <DashboardTimelineHeader />
        {/* TimelineStrip auto-detects the "current" phase (first Doing, else
            first Idle) — no more hardcoded "ruw" anchor that lit up Ruwbouw
            on every project. */}
        <TimelineStrip />
      </Card>

      <div className={styles.bottomGrid}>
        <Card className={styles.bottomCard}>
          <div className="row">
            <h3>Deze week</h3>
            <span className="ml-auto eyebrow">openstaande taken</span>
          </div>
          <QueryBoundary query={tasks} loading="Taken laden…">
            {(page) => <WeekList items={page.items.slice(0, 4)} />}
          </QueryBoundary>
        </Card>

        <Card className={styles.bottomCard}>
          <h3>Recente activiteit</h3>
          <QueryBoundary query={activity} loading="Activiteit laden…">
            {(page) => (
              <div className={`col gap-12 ${styles.activityList}`}>
                {page.items.slice(0, 5).map((entry) => (
                  <ActivityRow key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </QueryBoundary>
        </Card>

        <Card className={`${styles.bottomCard} ${styles.decisionsCard}`}>
          <div className="row gap-6">
            <Icon name="decision" size={16} color="var(--accent-2)" />
            <h3 className={styles.decisionsTitle}>Wacht op jouw beslissing</h3>
          </div>
          <p className={styles.decisionsLead}>Aannemer kan pas verder als je deze keuzes maakt.</p>
          <QueryBoundary query={dashboard} loading="Beslissingen laden…">
            {(data) => (
              <div className={`col gap-8 ${styles.decisionList}`}>
                {data.openDecisions.items.map((d) => (
                  <div key={d.id} className={styles.decisionRow}>
                    <div className="row">
                      <div className={styles.decisionRowTitle}>{d.title}</div>
                      <span className={`ml-auto mono ${styles.decisionRowDue}`}>
                        {formatRelativeDeadline(d.deadline)}
                      </span>
                    </div>
                    <div className={styles.decisionRowSub}>
                      {d.options.length} opties beschikbaar
                    </div>
                  </div>
                ))}
              </div>
            )}
          </QueryBoundary>
        </Card>
      </div>
    </AppFrame>
  );
}

type QuickAddKind = 'task' | 'photo' | 'document' | 'invoice' | 'decision';

const QUICK_ADD_ITEMS: Array<{ key: QuickAddKind; label: string; icon: IconName }> = [
  { key: 'task', label: 'Nieuwe taak', icon: 'check' },
  { key: 'photo', label: 'Foto uploaden', icon: 'camera' },
  { key: 'document', label: 'Document uploaden', icon: 'folder' },
  { key: 'invoice', label: 'Nieuwe factuur', icon: 'paperclip' },
  { key: 'decision', label: 'Nieuwe beslissing', icon: 'decision' },
];

function DashboardHero({ data }: { data: DashboardDto }) {
  // 2026-05-17 — greeting uses the signed-in user's first name (from
  // Clerk's first_name / fullName claim) instead of the seeded "Lieve".
  const { user } = useUser();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // Which quick-add create dialog is open (null = none). Dialogs are mounted
  // only while active so their data hooks don't run on every dashboard load.
  const [quickAdd, setQuickAdd] = useState<QuickAddKind | null>(null);
  const firstName =
    user?.firstName ||
    user?.fullName?.split(' ')[0] ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    'daar';
  const closeQuickAdd = () => setQuickAdd(null);
  return (
    <div className={`row ${styles.greeting}`}>
      <div>
        <div className="eyebrow">{data.projectAddress}</div>
        <h1 className={styles.title}>Goeiemorgen, {firstName}.</h1>
        <p className={styles.greetingSub}>
          {toNumber(data.openDecisions.count) > 0
            ? `${toNumber(data.openDecisions.count)} beslissing${toNumber(data.openDecisions.count) === 1 ? '' : 'en'} heeft jouw aandacht nodig.`
            : 'Geen openstaande beslissingen — alles loopt.'}
        </p>
      </div>
      <div className={`row gap-8 ml-auto ${styles.greetingActions}`}>
        <Btn onClick={() => navigate('/werf-modus')}>
          <Icon name="camera" size={14} /> Werfbezoek
        </Btn>
        <div className={styles.quickAddWrap}>
          <Btn
            variant="primary"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Icon name="plus" size={14} /> Snel toevoegen
          </Btn>
          {menuOpen && (
            <>
              <button
                type="button"
                className={styles.menuBackdrop}
                aria-label="Sluit menu"
                tabIndex={-1}
                onClick={() => setMenuOpen(false)}
              />
              <div role="menu" className={styles.quickMenu}>
                {QUICK_ADD_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setQuickAdd(item.key);
                    }}
                  >
                    <Icon name={item.icon} size={13} color="var(--ink-3)" /> {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {quickAdd === 'task' && <TaskCreateDialog open onOpenChange={closeQuickAdd} />}
      {quickAdd === 'photo' && <PhotoUploadDialog open onOpenChange={closeQuickAdd} />}
      {quickAdd === 'document' && <DocumentUploadDialog open onOpenChange={closeQuickAdd} />}
      {quickAdd === 'invoice' && <InvoiceCreateDialog open onOpenChange={closeQuickAdd} />}
      {quickAdd === 'decision' && <DecisionCreateDialog open onOpenChange={closeQuickAdd} />}
    </div>
  );
}

function KpiGrid({
  data,
  tasksData,
  tasksLoading,
  paidTotal,
  openTotal,
}: {
  data: DashboardDto;
  tasksData: { items: TaskDto[]; total: number | string } | undefined;
  tasksLoading: boolean;
  paidTotal: number;
  openTotal: number;
}) {
  const phase = data.currentPhase;
  const budget = data.budget;
  // Live "besteed" = everything booked (open + paid), matching the Budget
  // page's "Reeds besteed". The bar decomposes it: green = paid, orange
  // overlay = still open. Percentages are derived from the budget total
  // (set via Instellingen) rather than the stale stored spentPct/pledgedPct.
  const total = toNumber(budget.total);
  const committed = paidTotal + openTotal;
  const paidPct = total > 0 ? paidTotal / total : 0;
  const openPct = total > 0 ? openTotal / total : 0;

  return (
    <div className={styles.kpiGrid}>
      <Card className={styles.kpiCard}>
        <div className="eyebrow">Huidige fase</div>
        <div className={`row gap-10 ${styles.kpiHeadline}`}>
          <div className={styles.kpiPhaseLabel}>{phase?.label ?? '—'}</div>
          {phase?.progress !== null && phase?.progress !== undefined && (
            <Pill kind="warn">{Math.round(toNumber(phase.progress) * 100)}% voltooid</Pill>
          )}
        </div>
        <div className={styles.barLg}>
          <span
            style={{
              width: `${Math.round(toNumber(phase?.progress ?? 0) * 100)}%`,
              background: 'var(--warn)',
            }}
          />
        </div>
        <div className={`row ${styles.kpiMeta}`}>
          <span>{phase ? `Gestart ${formatDayMonth(phase.start)}` : ''}</span>
          <span className="ml-auto">
            {phase ? `Geplande einddatum ${formatDayMonth(phase.end)}` : ''}
          </span>
        </div>
      </Card>

      <Card className={styles.kpiCard}>
        <div className="eyebrow">Budget</div>
        <div className={styles.kpiNumber}>
          {formatCurrencyEUR(committed)}{' '}
          <span className={styles.kpiNumberSub}>/ {formatCurrencyEUR(total)}</span>
        </div>
        <div className={styles.barLg}>
          <span style={{ width: `${paidPct * 100}%`, background: 'var(--ok)' }} />
          <span
            className={styles.barOverlay}
            style={{
              left: `${paidPct * 100}%`,
              width: `${openPct * 100}%`,
              background: 'var(--warn)',
            }}
          />
        </div>
        <div className={`row ${styles.kpiLegend}`}>
          <span>
            <span className={`bg-ok ${styles.legendDot}`} />
            Betaald {Math.round(paidPct * 100)}%
          </span>
          <span className={styles.legendNext}>
            <span className={`bg-warn ${styles.legendDot}`} />
            Openstaand {Math.round(openPct * 100)}%
          </span>
        </div>
      </Card>

      <Card className={styles.kpiCard}>
        <div className="eyebrow">Openstaande facturen</div>
        <div className={styles.kpiNumber}>
          {formatCurrencyEUR(toNumber(data.openInvoices.total))}{' '}
          <span className={styles.kpiNumberSub}>· {toNumber(data.openInvoices.count)} stuks</span>
        </div>
        <div className={`col gap-4 ${styles.kpiList}`}>
          {data.openInvoices.items.map((inv) => {
            const due = formatRelativeDeadline(inv.dueAt);
            const tone = dueTone(due);
            return (
              <div key={inv.id} className="row">
                <span>{inv.supplier}</span>
                <span className={`ml-auto mono ${DUE_TONE_CLASS[tone]}`}>{due}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className={styles.kpiCard}>
        <div className="eyebrow">Mijn taken</div>
        <div className={styles.kpiNumber}>
          {tasksLoading ? '—' : toNumber(tasksData?.total ?? 0)}{' '}
          <span className={styles.kpiNumberSub}>open</span>
        </div>
        <div className={`col gap-4 ${styles.kpiList}`}>
          {(tasksData?.items ?? []).slice(0, 3).map((t) => (
            <div key={t.id} className="row gap-6">
              <Icon name="check" size={12} color={TASK_ICON_COLOR[taskTone(t)]} />
              <span>{t.title}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function WeekList({ items }: { items: TaskDto[] }) {
  if (items.length === 0) {
    return <div className="mono c-ink-3">Geen openstaande taken deze week.</div>;
  }
  return (
    <div className={`col ${styles.weekList}`}>
      {items.map((t, i) => (
        <WeekRow key={t.id} task={t} last={i === items.length - 1} />
      ))}
    </div>
  );
}

function WeekRow({ task, last }: { task: TaskDto; last: boolean }) {
  const due = formatRelativeDeadline(task.due);
  const tone = task.urgent ? 'bad' : task.status === 'InProgress' ? 'warn' : 'info';
  return (
    <div className={`row gap-12 ${styles.weekRow} ${last ? styles.weekRowLast : ''}`}>
      <div className={styles.weekTime}>
        <div className={`mono ${styles.weekTimeText}`}>{formatDayMonth(task.due)}</div>
        <Pill kind={tone}>{due}</Pill>
      </div>
      <div className="flex-1">
        <div className={styles.weekTitle}>{task.title}</div>
        <div className={styles.weekSub}>
          {task.assignedTo}
          {task.note ? ` · ${task.note}` : ''}
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ entry }: { entry: ActivityEntryDto }) {
  const payload = parsePayload<Record<string, unknown>>(entry.payloadJson);
  const detail = describeActivity(entry, payload);
  return (
    <div className={`row gap-10 ${styles.activityRow}`}>
      <div className={styles.activityIconWrap}>
        <Icon name={iconForVerb(entry.verb)} size={13} color="var(--ink-2)" />
      </div>
      <div className="flex-1">
        <div className={styles.activityText}>
          <strong className={styles.activityWho}>{entry.actorDisplayName}</strong>{' '}
          <span className={styles.activityWhat}>{dutchVerb(entry.verb)}</span>
        </div>
        <div className={styles.activityDetail}>
          {detail} · <span className="mono">{formatRelativePast(entry.at)}</span>
        </div>
      </div>
    </div>
  );
}

function describeActivity(
  entry: ActivityEntryDto,
  payload: Record<string, unknown> | undefined,
): string {
  if (!payload) return entry.targetType;
  if (entry.verb === 'photo.added') {
    const count = payload.count;
    const room = payload.room;
    return `${count ?? ''}${room ? ` foto's · ${String(room)}` : "foto's"}`;
  }
  if (entry.verb === 'invoice.received') {
    return `${String(payload.supplier ?? '')} · ${payload.amount ?? ''}`;
  }
  if (entry.verb === 'document.uploaded') {
    return String(payload.name ?? entry.targetType);
  }
  if (entry.verb === 'mail.sent') {
    return String(payload.subject ?? entry.targetType);
  }
  if (entry.verb === 'task.done') {
    return String(payload.title ?? entry.targetType);
  }
  return entry.targetType;
}
